import React from 'react';
import { FormattedMessage } from '../../util/reactIntl';
import { formatDate, formatDateOnly } from '../../util/dates';
import { formatMoney } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';
import { BsCircle, BsChevronDown, BsChevronUp } from 'react-icons/bs';
import { IoMdCheckmarkCircle, IoMdCheckmarkCircleOutline } from 'react-icons/io';
import { PrimaryButton, SecondaryButton } from '../../components';
import classNames from 'classnames';

import css from './TransactionPanel.css';

const { Money } = sdkTypes;

const ProcessStep = ({
  step,
  status, // 'pending', 'current', 'completed'
  timestamp,
  intl,
  isLast,
  actionButton,
  instructionText,
  showInstruction,
  customOfferInfo,
  customOfferData,
  isCustomer,
  onAcceptCustomOffer,
  acceptOfferInProgress,
  onOpenOfferModal,
  vatTotals,
  showCustomerVatSuffix,
  showProviderVatSuffix,
  customStepTitle,
  openCustomOfferModalButton,
  isPendingOffer,
  showOfferButton,
  onOpenInvoiceModal,
  receiptButton,
}) => {
  const todayString = intl.formatMessage({ id: 'ActivityFeed.today' });

  // Determine if action is needed (current step with action button or pending offer)
  const hasActionRequired = status === 'current' && (actionButton || isPendingOffer);

  const getStatusIcon = () => {
    if (status === 'completed') return <IoMdCheckmarkCircle className={css.stepIconCompleted} />;
    if (status === 'current') {
      if (hasActionRequired) {
        return <IoMdCheckmarkCircleOutline className={css.stepIconCurrentActionNeeded} />;
      }
      return <IoMdCheckmarkCircle className={css.stepIconCurrent} />;
    }
    return <BsCircle className={css.stepIconPending} />;
  };

  const stepClasses = classNames(css.processStep, {
    [css.stepCompleted]: status === 'completed',
    [css.stepCurrent]: status === 'current' && !hasActionRequired,
    [css.stepCurrentActionNeeded]: hasActionRequired,
    [css.stepPending]: status === 'pending',
    [css.stepLast]: isLast,
  });

  const stepContentClass = hasActionRequired ? css.stepContentActionNeeded : css.stepContent;

  const isCurrent = status === 'current';

  const renderShowOfferButton = () => {
    if (!showOfferButton || !onOpenInvoiceModal) return null;

    return (
      <div className={css.stepAction}>
        <PrimaryButton
          className={css.openCustomOfferModalButton}
          onClick={onOpenInvoiceModal}
          type="button"
        >
          <FormattedMessage id="ProcessSteps.showInvoiceInfo" />
        </PrimaryButton>
      </div>
    );
  };

  return (
    <div className={stepClasses}>
      <div className={css.stepConnector}>
        {getStatusIcon()}
        {!isLast && <div className={css.stepLine} />}
      </div>

      <div className={stepContentClass}>
        <div className={css.stepHeader}>
          <h4 className={css.stepTitle}>
            <FormattedMessage id={customStepTitle || step.title} />
          </h4>
        </div>

        {timestamp && status === 'completed' && (
          <p className={css.stepTimestamp}>{formatDate(intl, todayString, timestamp)}</p>
        )}

        {instructionText && (
          <p className={css.stepInstruction}>
            <FormattedMessage
              id={instructionText}
              values={{
                invoiceLink: (
                  <a
                    href="https://help.freedomly.io/fi/articles/6727816-freedomly-kevytyrittajalle"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FormattedMessage id="TransactionSteps.invoiceLinkText" />
                  </a>
                ),
              }}
            />
          </p>
        )}

        {/* Current step details */}
        {isCurrent && showInstruction && (
          <div className={css.currentStepDetails}>
            {/* Custom offer info for providers */}
            {customOfferInfo && (
              <div className={css.customOfferBox}>
                <div className={css.customOfferContent}>
                  <p className={css.customOfferReminder}>
                    <FormattedMessage id="ProcessSteps.platformFeeReminder" />
                  </p>
                  <div className={css.pricingExample}>
                    <div className={css.pricingExampleTitle}>
                      <FormattedMessage id="ProcessSteps.pricingExampleTitle" />
                    </div>
                    <div className={css.pricingRow}>
                      <span>
                        <FormattedMessage id="ProcessSteps.example" />
                      </span>
                      <span className={css.pricingAmount}>100 €</span>
                    </div>
                    <div className={css.pricingRow}>
                      <span className={css.netPayLabel}>
                        <FormattedMessage id="ProcessSteps.netPay" />
                      </span>
                      <span className={css.netPayAmount}>85 €</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Custom offer details */}
            {customOfferData && (
              <div className={css.customOfferSection}>
                {/* Absent on offers made before the start date field existed. */}
                {customOfferData.startDate && (
                  <div className={css.deliveryDateCompact}>
                    <span className={css.deliveryDateLabel}>
                      <FormattedMessage id="ProcessSteps.startDate" />
                    </span>
                    <span className={css.deliveryDateValue}>
                      {formatDateOnly(intl, todayString, new Date(customOfferData.startDate))}
                    </span>
                  </div>
                )}

                {customOfferData.deliveryDate && (
                  <div className={css.deliveryDateCompact}>
                    <span className={css.deliveryDateLabel}>
                      <FormattedMessage id="ProcessSteps.deliveryDate" />
                    </span>
                    <span className={css.deliveryDateValue}>
                      {formatDateOnly(intl, todayString, new Date(customOfferData.deliveryDate))}
                    </span>
                  </div>
                )}

                <div className={css.offerPricingCompact}>
                  <div className={css.pricingCompactRow}>
                    <span className={css.pricingCompactLabel}>
                      <FormattedMessage
                        id={
                          isCustomer
                            ? 'ProcessSteps.totalToPayShort'
                            : 'ProcessSteps.youWillEarnFinal'
                        }
                      />
                    </span>
                    <span className={css.pricingCompactValue}>
                      {isCustomer ? (
                        <>
                          {formatMoney(intl, new Money(customOfferData.amount, 'EUR'))}
                          {showCustomerVatSuffix && (
                            <>
                              {' '}
                              <FormattedMessage id="ProcessSteps.vatSuffix" />
                            </>
                          )}
                        </>
                      ) : (
                        <>
                          {formatMoney(intl, new Money(vatTotals.providerNet, 'EUR'))}
                          {showProviderVatSuffix && (
                            <>
                              {' '}
                              <FormattedMessage id="ProcessSteps.vatSuffix" />
                            </>
                          )}
                        </>
                      )}
                    </span>
                  </div>
                </div>

                <div className={css.offerDetailsButton}>
                  {isPendingOffer ? (
                    <PrimaryButton
                      className={css.openCustomOfferModalButton}
                      onClick={onOpenOfferModal}
                    >
                      <FormattedMessage id="ProcessSteps.viewFullDetails" />
                    </PrimaryButton>
                  ) : (
                    <SecondaryButton
                      className={css.openCustomOfferModalButton}
                      onClick={onOpenOfferModal}
                    >
                      <FormattedMessage id="ProcessSteps.viewFullDetails" />
                    </SecondaryButton>
                  )}
                </div>
              </div>
            )}

            {openCustomOfferModalButton && (
              <div className={css.stepAction}>{openCustomOfferModalButton}</div>
            )}

            {actionButton && <div className={css.stepAction}>{actionButton}</div>}
          </div>
        )}

        {/* Completed-step extras: invoice info for light entrepreneurs and the
            receipt download (offer-sent / payment-released / job-accepted) */}
        {(showOfferButton || receiptButton) && (
          <div className={css.stepExtraActions}>
            {renderShowOfferButton()}
            {receiptButton}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProcessStep;
